import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * Belcien cart drawer upsell: "Add"-knoppen in de drawer.
 * Kleur en maat zijn aparte keuzelijsten; hier zoeken we de bijbehorende variant.
 * Bij een andere kleur wisselt de foto mee naar de variant-foto.
 * Event delegation, zodat het blijft werken nadat Horizon de drawer opnieuw rendert.
 * Knoptekst komt uit data-label (Thema-instellingen → Belcien cart drawer).
 */
if (!window.__belcienCartUpsell) {
  window.__belcienCartUpsell = true;

  const findVariant = (form) => {
    const dataEl = form.querySelector('[data-bc-variants]');
    const idInput = form.querySelector('input[name="id"]');
    if (!dataEl) return idInput ? Number(idInput.value) : 0;
    let variants = [];
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return idInput ? Number(idInput.value) : 0;
    }
    const picks = [...form.querySelectorAll('[data-bc-opt]')].map((s) => s.value);
    const match = variants.find((v) => picks.every((p, i) => v[i + 1] === p));
    if (match) return { id: match[0], available: match[3], img: match[4] || null };

    // Geen exacte combinatie: toch de foto van de gekozen kleur tonen
    const colorPick = picks[0];
    const sameColor = variants.find((v) => v[1] === colorPick && v[4]) || variants.find((v) => v[2] === picks[1] && v[4]);
    return { id: null, available: false, img: sameColor ? sameColor[4] : null };
  };

  const label = (button) => button?.dataset.label || 'Add';

  const swapImage = (form, src) => {
    if (!src) return;
    const card = form.closest('.bc-upsell__card');
    const img = card?.querySelector('.bc-upsell__img');
    if (!img || img.getAttribute('src') === src) return;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.loading = 'eager';
    img.setAttribute('src', src);
  };

  const syncForm = (form) => {
    const res = findVariant(form);
    const button = form.querySelector('button[type="submit"]');
    const idInput = form.querySelector('input[name="id"]');
    if (res && typeof res === 'object') {
      swapImage(form, res.img);
      if (res.id && idInput) idInput.value = res.id;
      if (button) {
        const ok = !!res.id && res.available;
        button.disabled = !ok;
        button.textContent = ok ? label(button) : 'Sold out';
      }
    }
  };

  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-bc-opt]')) return;
    const form = select.closest('[data-bc-upsell]');
    if (form) syncForm(form);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-bc-upsell]')) return;
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    const id = Number(new FormData(form).get('id'));
    if (!id || button?.disabled) return;

    if (button) {
      button.disabled = true;
      button.textContent = 'Adding…';
    }

    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      });
      if (!res.ok) throw new Error(`Add failed: ${res.status}`);
      const cart = await fetch(`${root}cart.js`).then((r) => r.json());

      document.dispatchEvent(
        new CartAddEvent(cart, 'belcien-cart-upsell', {
          source: 'belcien-cart-upsell',
          itemCount: cart.item_count,
          variantId: String(id),
        })
      );
    } catch (error) {
      console.error(error);
      if (button) {
        button.disabled = false;
        button.textContent = 'Try again';
      }
    }
  });
}

/**
 * Belcien speed: cart drawer opent direct bij "Add to cart" (optimistisch).
 * We wachten niet meer op de server: de drawer schuift meteen open met een laadbalk,
 * en zodra de cart terug is vult Horizon de inhoud in (morph). Bij een fout sluit de drawer weer,
 * zodat de klant de foutmelding bij de knop ziet.
 * Daarnaast: afbeeldingen in de drawer vooraf laden, zodat er geen lege vakjes zijn bij openen.
 */
if (!window.__belcienCartFast) {
  window.__belcienCartFast = true;

  const getDrawer = () => document.querySelector('cart-drawer-component');
  const getDialog = () => getDrawer()?.querySelector('dialog');
  let pendingTimer = 0;

  const clearPending = () => {
    clearTimeout(pendingTimer);
    getDialog()?.classList.remove('bc-pending');
  };

  const warmImages = () => {
    document.querySelectorAll('cart-drawer-component img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.matches('[data-bc-upsell]')) return;
      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add')) return;
      if (form.closest('dialog')) return;

      const drawer = getDrawer();
      const dialog = getDialog();
      if (!drawer || !dialog || !drawer.hasAttribute('auto-open')) return;

      const button = event.submitter || form.querySelector('[type="submit"]');
      if (button && (button.disabled || button.getAttribute('aria-disabled') === 'true')) return;

      warmImages();
      dialog.classList.add('bc-pending');
      clearTimeout(pendingTimer);
      pendingTimer = window.setTimeout(clearPending, 8000);

      if (typeof drawer.open === 'function') drawer.open();
      else if (typeof drawer.showDialog === 'function') drawer.showDialog();
    },
    true
  );

  document.addEventListener(ThemeEvents.cartUpdate, () => {
    clearPending();
    requestAnimationFrame(warmImages);
  });

  document.addEventListener(ThemeEvents.cartError, () => {
    const wasPending = getDialog()?.classList.contains('bc-pending');
    clearPending();
    if (wasPending) getDrawer()?.close?.();
  });

  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
  if (document.readyState === 'complete') idle(warmImages);
  else window.addEventListener('load', () => idle(warmImages), { once: true });

  document.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-testid="cart-drawer-trigger"]')) warmImages();
    },
    { passive: true, capture: true }
  );
}

/**
 * Belcien: verouderde cart na "terug" in de browser (vooral iPhone Safari/Chrome).
 * Bij terugnavigeren toont de browser soms een oude kopie van de pagina, met een lege of verouderde
 * cart drawer en capsule bar, terwijl de echte cart wel items heeft. We vergelijken het aantal items
 * in de drawer met /cart.js en laden de pagina één keer opnieuw als het niet klopt.
 */
if (!window.__belcienCartSync) {
  window.__belcienCartSync = true;

  const renderedCount = () => {
    const inputs = document.querySelectorAll('cart-drawer-component .cart-items .quantity-selector input');
    let n = 0;
    inputs.forEach((i) => { n += parseInt(i.value, 10) || 0; });
    return n;
  };

  const isBackNav = () => {
    const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    return !!(nav && nav.type === 'back_forward');
  };

  let busy = false;
  const check = async () => {
    if (busy) return;
    busy = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cart = await fetch(`${root}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (cart.item_count === renderedCount()) return;
      const key = 'bc-cart-resync';
      const last = Number(sessionStorage.getItem(key) || 0);
      if (Date.now() - last < 15000) return;
      sessionStorage.setItem(key, String(Date.now()));
      window.location.reload();
    } catch (e) {
      // stil falen: nooit de pagina blokkeren
    } finally {
      busy = false;
    }
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted || isBackNav()) check();
  });
  if (document.readyState === 'complete' && isBackNav()) check();
}